import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  Clock, Play, Square, Plus, Loader2, Calendar, Target, Timer
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CRMTimeTracking() {
  const { token, user } = useCRMAuth();
  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState('');
  const [logHours, setLogHours] = useState('');
  const [logDate, setLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [logNotes, setLogNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchActiveTimer();
    fetchSummary();
    fetchTasks();
  }, []);

  useEffect(() => {
    if (activeTimer) {
      timerRef.current = setInterval(() => {
        const start = new Date(activeTimer.started_at).getTime();
        const now = Date.now();
        setElapsed(Math.floor((now - start) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setElapsed(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [activeTimer]);

  const fetchActiveTimer = async () => {
    try {
      const res = await fetch(`${API}/api/crm/time/timer/active?user_id=${user?.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.active) setActiveTimer(data);
        else setActiveTimer(null);
      }
    } catch (err) {
      // Silent fail - timer state will remain unchanged
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await fetch(`${API}/api/crm/time/user/${user?.id}/summary?period=week`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setSummary(await res.json());
    } catch (err) {
      // Silent fail - summary will show empty
    }
    finally { setLoading(false); }
  };

  const fetchTasks = async () => {
    try {
      const res = await fetch(`${API}/api/crm/tasks/?status=in_progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
      }
    } catch (err) {
      // Silent fail - tasks will show empty
    }
  };

  const startTimer = async (taskId) => {
    try {
      const res = await fetch(`${API}/api/crm/time/timer/start?user_id=${user?.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: taskId })
      });
      if (res.ok) {
        toast.success('Timer started!');
        fetchActiveTimer();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch (err) { toast.error('Failed to start'); }
  };

  const stopTimer = async () => {
    try {
      const res = await fetch(`${API}/api/crm/time/timer/stop?user_id=${user?.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Logged ${data.duration_hours} hours`);
        setActiveTimer(null);
        fetchSummary();
      }
    } catch (err) { toast.error('Failed to stop'); }
  };

  const handleLogTime = async () => {
    if (!selectedTask || !logHours) {
      toast.error('Select task and enter hours');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/time/entry?user_id=${user?.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task_id: selectedTask,
          hours: parseFloat(logHours),
          date: logDate,
          notes: logNotes
        })
      });
      if (res.ok) {
        toast.success('Time logged!');
        setShowLogModal(false);
        setLogHours('');
        setLogNotes('');
        fetchSummary();
      }
    } catch (err) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <CRMLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Time Tracking</h1>
          <p className="text-slate-500">Track time spent on tasks</p>
        </div>
        <Button onClick={() => setShowLogModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" /> Log Time Manually
        </Button>
      </div>

      {/* Active Timer */}
      <Card className={`p-6 mb-6 ${activeTimer ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white' : ''}`}>
        {activeTimer ? (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Timer className="w-7 h-7 animate-pulse" />
              </div>
              <div>
                <p className="text-sm opacity-80">Currently tracking</p>
                <p className="text-lg font-semibold">{activeTimer.task_title}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-4xl font-mono font-bold">{formatTime(elapsed)}</div>
              <Button onClick={stopTimer} size="lg" className="bg-white text-indigo-600 hover:bg-white/90">
                <Square className="w-5 h-5 mr-2" /> Stop
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center">
                <Clock className="w-7 h-7 text-slate-400" />
              </div>
              <div>
                <p className="text-slate-500">No active timer</p>
                <p className="text-sm text-slate-400">Select a task below to start tracking</p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Weekly Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">This Week</p>
              <p className="text-2xl font-bold text-slate-900">{summary?.total_hours || 0} hrs</p>
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Tasks Tracked</p>
              <p className="text-2xl font-bold text-slate-900">{summary?.by_task?.length || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Daily Average</p>
              <p className="text-2xl font-bold text-slate-900">{((summary?.total_hours || 0) / 7).toFixed(1)} hrs</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tasks to Track */}
      <Card className="p-5">
        <h3 className="font-semibold text-slate-900 mb-4">Quick Start Timer</h3>
        {tasks.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No in-progress tasks</p>
        ) : (
          <div className="space-y-2">
            {tasks.slice(0, 5).map(task => (
              <div key={task.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                <div>
                  <p className="font-medium text-slate-900">{task.title}</p>
                  <p className="text-sm text-slate-500">{task.client_name}</p>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => startTimer(task.id)}
                  disabled={!!activeTimer}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Play className="w-4 h-4 mr-1" /> Start
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Log Time Modal */}
      <Dialog open={showLogModal} onOpenChange={setShowLogModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log Time Manually</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Task</label>
              <Select value={selectedTask} onValueChange={setSelectedTask}>
                <SelectTrigger><SelectValue placeholder="Select task" /></SelectTrigger>
                <SelectContent>{tasks.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Hours</label>
                <Input type="number" step="0.25" min="0.25" value={logHours} onChange={(e) => setLogHours(e.target.value)} placeholder="2.5" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Date</label>
                <Input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea value={logNotes} onChange={(e) => setLogNotes(e.target.value)} placeholder="What did you work on?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogModal(false)}>Cancel</Button>
            <Button onClick={handleLogTime} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log Time'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CRMLayout>
  );
}
